import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { BREAKPOINTS } from '../constants';
import {
  AnalyticsConsent,
  AnalyticsConsentDecision,
  getAnalyticsConsent,
  isAnalyticsConsentPersisted,
  setAnalyticsConsent,
  subscribeAnalyticsConsent,
} from '../utils/analyticsConsent';
import { isSensitiveAnalyticsPath } from '../analytics/routePrivacy';

const Banner = styled.aside`
  position: fixed;
  right: 1rem;
  bottom: calc(1rem + env(safe-area-inset-bottom));
  z-index: 1200;
  width: min(34rem, calc(100vw - 2rem));
  padding: 1rem;
  border: 1px solid var(--border);
  border-radius: 12px;
  background: color-mix(in srgb, var(--surface) 97%, var(--primary) 3%);
  color: var(--text);
  box-shadow: 0 12px 32px var(--shadow);

  @media (max-width: ${BREAKPOINTS.MOBILE}px) {
    right: 0.75rem;
    bottom: calc(4.75rem + env(safe-area-inset-bottom));
    left: 0.75rem;
    width: auto;
    padding: 0.85rem;
  }
`;

const BannerTitle = styled.h2`
  margin: 0 0 0.35rem;
  color: var(--text);
  font-size: 1rem;
  line-height: 1.3;
`;

const Copy = styled.p`
  margin: 0;
  color: var(--text-secondary);
  font-size: 0.85rem;
  line-height: 1.45;
`;

const PrivacyLink = styled(Link)`
  color: var(--primary);
  font-weight: 700;
`;

const ChoiceGroup = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.6rem;
  margin-top: 0.8rem;

  @media (max-width: 360px) {
    grid-template-columns: 1fr;
  }
`;

const ChoiceButton = styled.button<{ $selected?: boolean }>`
  min-height: 44px;
  padding: 0.6rem 0.75rem;
  border: 1px solid ${({ $selected }) => ($selected ? 'var(--primary)' : 'var(--border)')};
  border-radius: 8px;
  background: ${({ $selected }) => (
    $selected
      ? 'color-mix(in srgb, var(--primary) 16%, var(--surface))'
      : 'var(--surface)'
  )};
  color: var(--text);
  font: inherit;
  font-size: 0.88rem;
  font-weight: 750;
  cursor: pointer;

  &:hover,
  &:focus-visible {
    border-color: var(--primary);
    background: color-mix(in srgb, var(--primary) 12%, var(--surface));
  }
`;

const ChoicePanel = styled.section`
  margin-top: 1rem;
  padding: 1rem;
  border: 1px solid var(--border);
  border-radius: 10px;
  background: color-mix(in srgb, var(--primary) 5%, var(--surface));
`;

const CurrentChoice = styled.p`
  margin: 0;
  color: var(--text);
  font-weight: 700;
`;

const ChoiceHelp = styled.p`
  margin: 0.4rem 0 0;
  color: var(--text-secondary);
  font-size: 0.85rem;
`;

const SaveStatus = styled.p`
  min-height: 1.3em;
  margin: 0.55rem 0 0;
  color: var(--text-secondary);
  font-size: 0.8rem;
`;

const useAnalyticsConsentChoice = () => {
  const [consent, setConsent] = useState<AnalyticsConsent>(() => getAnalyticsConsent());
  const [persisted, setPersisted] = useState(() => isAnalyticsConsentPersisted());

  useEffect(() => subscribeAnalyticsConsent((nextConsent, change) => {
    setConsent(nextConsent);
    setPersisted(change.persisted);
  }), []);

  const choose = (decision: AnalyticsConsentDecision) => {
    const saved = setAnalyticsConsent(decision);
    setConsent(decision);
    setPersisted(saved);
  };

  return { consent, persisted, choose };
};

export const AnalyticsConsentBanner: React.FC = () => {
  const { consent, choose } = useAnalyticsConsentChoice();
  const location = useLocation();
  const isPrivacyPage = /^\/privacy\/?$/i.test(location.pathname);

  if (consent !== 'unknown' || isPrivacyPage || isSensitiveAnalyticsPath(location.pathname)) return null;

  return (
    <Banner role="region" aria-labelledby="analytics-consent-heading">
      <BannerTitle id="analytics-consent-heading">Optional product analytics</BannerTitle>
      <Copy>
        Anonymous, cookie-free page counts are collected on public pages. You can also help us
        understand which calendar tools are useful. We do not send your search words, contact
        details or precise location.{' '}
        <PrivacyLink to="/privacy#analytics-choices">Privacy details</PrivacyLink>
      </Copy>
      <ChoiceGroup role="group" aria-label="Analytics preference">
        <ChoiceButton type="button" onClick={() => choose('denied')}>
          Decline analytics
        </ChoiceButton>
        <ChoiceButton type="button" onClick={() => choose('granted')}>
          Allow analytics
        </ChoiceButton>
      </ChoiceGroup>
    </Banner>
  );
};

const choiceLabel = (consent: AnalyticsConsent) => {
  if (consent === 'granted') return 'Optional product analytics are allowed on this browser.';
  if (consent === 'denied') return 'Optional product analytics are declined on this browser.';
  return 'No optional product analytics choice is saved. Optional product analytics remain off.';
};

export const AnalyticsConsentControl: React.FC = () => {
  const { consent, persisted, choose } = useAnalyticsConsentChoice();
  const [hasChosenHere, setHasChosenHere] = useState(false);

  const handleChoice = (decision: AnalyticsConsentDecision) => {
    choose(decision);
    setHasChosenHere(true);
  };

  return (
    <ChoicePanel id="analytics-choices" aria-labelledby="analytics-choice-heading" tabIndex={-1}>
      <CurrentChoice id="analytics-choice-heading" role="status" aria-live="polite">
        {choiceLabel(consent)}
      </CurrentChoice>
      <ChoiceHelp>
        You can change this optional choice at any time. Declining does not affect event discovery,
        saved events or account features.
      </ChoiceHelp>
      <ChoiceGroup role="group" aria-label="Change analytics preference">
        <ChoiceButton
          type="button"
          $selected={consent === 'denied'}
          aria-pressed={consent === 'denied'}
          onClick={() => handleChoice('denied')}
        >
          Decline analytics
        </ChoiceButton>
        <ChoiceButton
          type="button"
          $selected={consent === 'granted'}
          aria-pressed={consent === 'granted'}
          onClick={() => handleChoice('granted')}
        >
          Allow analytics
        </ChoiceButton>
      </ChoiceGroup>
      <SaveStatus aria-live="polite">
        {hasChosenHere && (persisted
          ? 'Your choice has been saved on this browser.'
          : 'Browser storage is unavailable, so this choice applies only for this visit.')}
      </SaveStatus>
    </ChoicePanel>
  );
};

export default AnalyticsConsentBanner;
